import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Search,
  ArrowRight,
  Building2,
  Monitor,
  Image,
  ListVideo,
  Calendar,
  CalendarClock,
  Folder,
  Rss,
  Smartphone,
  Users2,
  User,
  X,
  Loader2,
  type LucideIcon,
} from 'lucide-react';
import { useIsAllTeamScope, useTeamNameResolver } from '@/hooks/useWorkspaceScope';
import { isEnterKey } from '@/lib/keyboard';
import { sanitizeInput } from '@/lib/sanitize-input';
import { HighlightText } from '@/components/shared/HighlightText';
import {
  collapseLocationTrail,
  collapseTrailSegments,
} from '@/features/search/utils/location-trail';
import { getRecencyShort } from '@/features/search/utils/all-view-helpers';
import { getContainerPath, getFolderTrailSegments } from '@/features/search/utils/search-columns';
import type { SearchEntityType } from '@/features/search/utils/entity-types';
import { EnterHint, KeyHint } from '@/features/search/components/EnterHint';
import { RecentSearchGroup } from '@/features/search/components/RecentSearchGroup';
import { useGlobalSearch } from '@/features/search/hooks/useGlobalSearch';
import { useSearchItemNav } from '@/features/search/hooks/useSearchItemNav';
import { useVisibleNavTargets } from '@/features/search/hooks/useVisibleNavTargets';
import { useSearchRecentsStore } from '@/features/search/stores/search-recents-store';
import { useTeamStore } from '@/stores/team-store';
import { useApolloClient } from '@apollo/client/react';
import { resolveNavAction } from '@/features/search/utils/nav-actions';
import { useSearchHeaderStore } from '@/features/search/stores/search-header-store';
import { SearchStateArt } from '@/features/search/components/SearchStateArt';
import {
  getActionLabels,
  matchNavTargetsGrouped,
  type NavTarget,
} from '@/features/search/utils/nav-targets';
import {
  GROUP_ORDER,
  getActionIcon,
  isEntityTypeVisible,
  MAX_QUERY_LENGTH,
  MIN_QUERY_LENGTH,
  getCurrentEntityType,
  getGroupLabels,
  getItemName,
  getRowKey,
} from '@/features/search/utils/entity-types';
import {
  Command,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Button,
  cn,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@scio3/ui';

/** Expanded width of the search bar + dropdown. The collapsed trigger is
 *  `w-64` (256px), so opening reads as the bar growing leftwards in place. */
const PANEL_WIDTH = 640;
/**
 * Height of the expanded search bar, matching the `h-9` field in the nav.
 *
 * The bar normally takes the MEASURED height of whatever it opened from, so it
 * reads as that control widening in place. That only works when the trigger is
 * the field itself: in compact nav the trigger is a 32px icon button, and
 * inheriting 32px rendered a squashed search bar with no relationship to
 * anything. There's nothing to impersonate in that case, so it falls back to its
 * own natural height.
 */
const BAR_HEIGHT = 36;
/** Keep the panel off the viewport edge when the trigger sits near it. */
const VIEWPORT_MARGIN = 16;
/** Must match the `duration-200` on the bar's width and the dropdown's
 *  fade/rise. Closing plays those transitions in reverse and only unmounts the
 *  portal once they've finished, so the exit mirrors the entrance instead of
 *  the whole thing vanishing on the frame `open` flips false. */
const ANIM_MS = 200;

/** The results page. On it, this component stops being a palette trigger and
 *  becomes that page's live query field — see `showPageField`. */
const RESULTS_PATH = '/app/search';
/** Same 300ms the palette's own input debounces on, so typing in either place
 *  costs the same number of round trips. */
const PAGE_QUERY_DEBOUNCE_MS = 300;

/** Non-matching sentinel so cmdk doesn't auto-highlight the first item */
const NO_SELECTION = '__none__';

interface GlobalSearchBarProps {
  compact?: boolean;
  /**
   * Give the field more room. Set by `TopNavigation` on the results page when
   * the nav isn't collapsing yet — there, search IS the page's main control, so
   * spare width is better spent on it than left as empty nav.
   */
  wide?: boolean;
}

export function GlobalSearchBar({ compact, wide = false }: GlobalSearchBarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  /**
   * All view: name each result's owning teamspace on its subtitle line.
   *
   * The dropdown searches every teamspace the caller reaches (its own query is deliberately
   * unfiltered), so in All view a result list can mix teamspaces with nothing saying which is which
   * — the same problem the results page solves with its Teamspace column. Outside All view this
   * stays `null` and the line is byte-identical to before.
   */
  const isAllTeamScope = useIsAllTeamScope();
  const resolveTeamName = useTeamNameResolver();
  /**
   * The teamspace a MEMBER row belongs to — resolved through the shared resolver so the root
   * teamspace reads "Main Teamspace" here exactly as it does on every other row.
   *
   * Shown unconditionally (not only in All view): a member row names the team the MEMBERSHIP belongs
   * to, which is what distinguishes two rows for the same person, and this line has always shown it.
   * Falls back to the row's own `teamName` when the id is outside the loaded team list.
   */
  const memberTeamName = useCallback(
    (item: Record<string, unknown>): string | null => {
      const rowTeamId = item.teamId;
      const resolved = typeof rowTeamId === 'string' && rowTeamId ? resolveTeamName(rowTeamId) : '';
      return resolved || (item.teamName as string) || null;
    },
    [resolveTeamName],
  );
  const workspaceNameFor = useCallback(
    (entityType: string, item: Record<string, unknown>): string | null => {
      if (!isAllTeamScope || !WORKSPACE_SUBTITLE_TYPES.has(entityType)) return null;
      const rowTeamId = item.teamId;
      // Guarded here, not in the resolver: `useTeamNameResolver` falls back to ROOT for a missing
      // id, so an unguarded call would label a teamspace-less row with the root teamspace's name.
      if (typeof rowTeamId !== 'string' || !rowTeamId) return null;
      return resolveTeamName(rowTeamId) || null;
    },
    [isAllTeamScope, resolveTeamName],
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState(NO_SELECTION);
  /**
   * Palette ordering. Component-local, NOT in the URL: the palette floats over
   * whatever page you're on, so writing `?sort=` would mutate that page's own
   * params. It survives close/reopen (the component stays mounted in the nav)
   * and rides along to `/app/search` when you jump to the full results.
   *
   * This is a real control even though the panel only shows five rows per
   * section: the sort is applied SERVER-side, so it changes WHICH five you get
   * — the five most recently edited matches rather than the five best-scoring.
   */
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  // Where to draw the panel: measured off the trigger so the panel hangs from
  // the search bar instead of floating in the middle of the viewport.
  const [anchor, setAnchor] = useState<{
    top: number;
    /** Distance from the viewport's RIGHT edge — the trigger's right edge. */
    right: number;
    width: number;
    /** Trigger height, so the expanded input matches the collapsed bar exactly. */
    height: number;
    /** The collapsed trigger's width — the start frame of the expand. */
    collapsedWidth: number;
  } | null>(null);
  /**
   * Drives the open animation. Starts false on every open so the bar renders at
   * the trigger's width for one frame, then flips true on the next frame and
   * CSS transitions it out to `PANEL_WIDTH`. Because the container is pinned by
   * its RIGHT edge, animating width is what makes it grow leftwards.
   */
  const [expanded, setExpanded] = useState(false);
  /** Pending unmount for an in-flight close. Non-null === mid-collapse. */
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current === null) return;
    clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);

  /**
   * Animated close: collapse FIRST (`expanded=false` runs the same two
   * transitions backwards), unmount after they land. Unmounting straight away
   * would skip the exit entirely, which is why `setOpen(false)` isn't called
   * directly from Esc / X / click-outside any more.
   * Reduced motion has no transition to wait for, so it unmounts immediately.
   */
  const closePalette = useCallback(() => {
    setExpanded(false);
    clearCloseTimer();
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setOpen(false);
      return;
    }
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, ANIM_MS);
  }, [clearCloseTimer]);

  /**
   * Reopening mid-collapse reverses the exit rather than restarting it: the
   * portal is still mounted, so cancelling the pending unmount and flipping
   * `expanded` back on picks the width transition up from wherever it got to.
   * On a cold open the layout effect below owns the first frame instead.
   */
  const openPalette = useCallback(() => {
    const wasClosing = closeTimer.current !== null;
    clearCloseTimer();
    if (wasClosing) setExpanded(true);
    setOpen(true);
  }, [clearCloseTimer]);

  const togglePalette = useCallback(() => {
    // Mid-collapse counts as closed — Cmd+K should bring it back, not re-close it.
    if (open && closeTimer.current === null) closePalette();
    else openPalette();
  }, [open, closePalette, openPalette]);

  // Never leave a timer to fire into an unmounted tree.
  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  /* ── Results-page inline query ──────────────────────────────────────────────
     On `/app/search` the nav box is no longer a "click me to open the palette"
     trigger: it holds the `?q=` that produced the results below it, editable in
     place, and typing re-searches the page. The page renders no input of its
     own (`SearchFilterBar` is filters + sort only), so before this the only way
     to amend a search was to reopen the palette and retype it from scratch.
     The URL stays the single source of truth — `SearchPage` reads `?q=` and
     Apollo owns the refetch — so this only has to keep that param current. */
  const [searchParams, setSearchParams] = useSearchParams();
  const isResultsPage = location.pathname === RESULTS_PATH;
  // Compact nav has no room for a text field, so it keeps the icon trigger —
  // but the palette-side live push below still applies on the results page.
  const showPageField = isResultsPage && !compact;
  /**
   * On the results page the field starts in the PAGE header and only comes back
   * here once you scroll (see `search-header-store`). The nav copy stays MOUNTED
   * and merely fades: hiding it outright would collapse 256px of nav and shunt
   * the help + notification icons sideways on every handoff, and `triggerRef`
   * would stop measuring — which is what the palette anchors to.
   */
  const isDocked = useSearchHeaderStore((state) => state.isDocked);
  const isHandedToPage = isResultsPage && !isDocked;
  const requestPageFieldFocus = useSearchHeaderStore((state) => state.requestPageFieldFocus);

  /**
   * Scrolling back to the top of the results page hands the field to the page —
   * so this one stops being the search box and has to let go of the search.
   *
   * It is only hidden (`opacity-0` + `aria-hidden`), never unmounted, so
   * without this it kept focus and kept its palette open: you ended up typing
   * into an invisible field with a dropdown covering the results, while the
   * page's own field sat below showing the same query. The palette closes, this
   * field blurs, and the page takes focus with the caret where it was.
   *
   * Only on the false→true edge — re-running while handed off would fight
   * anyone who scrolled back down and clicked into this field again.
   */
  const wasHandedToPage = useRef(isHandedToPage);
  useEffect(() => {
    const justHandedOff = isHandedToPage && !wasHandedToPage.current;
    wasHandedToPage.current = isHandedToPage;
    if (!justHandedOff) return;
    // Only pass focus on if this field actually HELD it. The handoff fires on
    // scroll depth alone, so scrolling down and back up without ever touching
    // search was dropping a caret into the page field and putting it in its
    // focus state, ready to swallow the user's next keystroke. Handing over
    // focus nobody had is not a continuation, it is a steal. `open` covers the
    // palette case, where focus sits on a result row rather than the input.
    const heldFocus =
      open || (inputRef.current !== null && document.activeElement === inputRef.current);
    setOpen(false);
    // Blur BEFORE asking the page to focus: a focus() while this still holds
    // focus is a no-op in some browsers, and the blur would then land after it.
    inputRef.current?.blur();
    if (heldFocus) requestPageFieldFocus();
    // `open` is read at the moment of the edge, not depended on — adding it
    // would re-run this effect on every palette toggle while already handed off.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHandedToPage, requestPageFieldFocus]);
  const urlQuery = searchParams.get('q') ?? '';
  const [pageQuery, setPageQuery] = useState(urlQuery);
  const pageInputRef = useRef<HTMLInputElement>(null);
  /** Caret offset carried across the field -> palette handoff (see below). */
  const handoffCaret = useRef<number | null>(null);
  /** Last `?q=` written from here, so the sync effect ignores its own echo. */
  const pushedQuery = useRef(urlQuery);
  const pageQueryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPageQueryTimer = useCallback(() => {
    if (pageQueryTimer.current === null) return;
    clearTimeout(pageQueryTimer.current);
    pageQueryTimer.current = null;
  }, []);

  const pushPageQuery = useCallback(
    (value: string) => {
      pushedQuery.current = value;
      // Keep the field's own text on the last pushed value, so it reads
      // correctly the moment the palette collapses off it.
      setPageQuery(value);
      setSearchParams(
        // Merge, never replace: `type` / `sort` / `dir` are the page's other
        // params and a bare `{ q }` would silently reset the user's filter.
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set('q', value);
          else next.delete('q');
          return next;
        },
        // `replace` so editing a 10-character query doesn't bury the page the
        // user came from under 10 history entries.
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /**
   * Debounced `?q=` write — the page's live re-search. Driven from BOTH inputs:
   * the field itself for the first keystroke, then the palette's input once the
   * handoff has happened, so the results underneath keep tracking what's typed
   * either way. One shared timer, so the two can't double up.
   */
  const schedulePageQueryPush = useCallback(
    (value: string) => {
      clearPageQueryTimer();
      pageQueryTimer.current = setTimeout(() => {
        pageQueryTimer.current = null;
        pushPageQuery(value);
      }, PAGE_QUERY_DEBOUNCE_MS);
    },
    [clearPageQueryTimer, pushPageQuery],
  );

  /** Enter skips the debounce — the user has committed to this query. */
  const flushPageQuery = useCallback(() => {
    clearPageQueryTimer();
    pushPageQuery(pageQuery);
  }, [clearPageQueryTimer, pageQuery, pushPageQuery]);

  const clearPageQuery = useCallback(() => {
    clearPageQueryTimer();
    pushPageQuery('');
    pageInputRef.current?.focus();
  }, [clearPageQueryTimer, pushPageQuery]);

  // Adopt `?q=` changes this field DIDN'T make: Enter from the palette, a
  // shared link, browser back/forward. Skipping our own echo is what keeps a
  // slow typist's in-flight text from being overwritten by the value we just
  // pushed a moment earlier.
  useEffect(() => {
    if (urlQuery === pushedQuery.current) return;
    pushedQuery.current = urlQuery;
    setPageQuery(urlQuery);
  }, [urlQuery]);

  useEffect(() => clearPageQueryTimer, [clearPageQueryTimer]);

  // Track whether user explicitly arrow-navigated (vs cmdk auto-selecting first item)
  const hasArrowNavigated = useRef(false);
  // Block cmdk auto-highlight until user interacts (arrow keys or mouse hover)
  const hasInteracted = useRef(false);

  // Two-query global search shared with the full search page. Dropdown uses
  // limit=5 (page uses 50). Fast paints at ~150ms; slow assets/engages bucket
  // fills in ~700-1500ms later behind the always-on "Searching..." footer.
  const {
    search,
    results,
    loading,
    assetsEngagesResults,
    assetsEngagesLoading,
    hasResults,
    cachedQuery,
    slowCachedQuery,
  } = useGlobalSearch({ limit: 5 });

  // Cache-match gates — the hook keeps the last-completed response as a
  // sticky fallback so incremental typing ("benc" → "bench") doesn't flash
  // an empty dropdown between the two responses. But when the user types
  // something unrelated ("bench" → "b;" or "bench" → "hello"), the sticky
  // cache is misleading. Bidirectional prefix match: show the cache when
  // one query is a prefix of the other (backspacing OR appending chars),
  // hide it otherwise. Empty cachedQuery (before any response has ever
  // landed) also hides — nothing meaningful to serve.
  //
  // Two gates, because the fast bucket (`cachedQuery`) and the slow
  // assets/engages bucket (`slowCachedQuery`) race and land at different
  // times:
  //   - `displayHasResults` — lenient (fast-only) prefix match. Used for
  //     the results block + trailing "Go to" separator, so incremental
  //     typing keeps rows visible even when the slow bucket is briefly
  //     mid-fetch.
  //   - `footerActive`-side — strict prefix match against BOTH trackers.
  //     `hasResults` is derived from union(fast, slow), so a stale slow
  //     bucket alongside a fresh fast bucket would prop it up long enough
  //     to flash the footer "All search results for X" active for the
  //     split second before the slow bucket also lands. Requiring both
  //     trackers to agree with the current input suppresses that flash.
  const trimmedQuery = query.trim();
  const cacheMatchesInput =
    cachedQuery !== '' &&
    (trimmedQuery.startsWith(cachedQuery) || cachedQuery.startsWith(trimmedQuery));
  const slowCacheMatchesInput =
    slowCachedQuery !== '' &&
    (trimmedQuery.startsWith(slowCachedQuery) || slowCachedQuery.startsWith(trimmedQuery));
  const displayHasResults = hasResults && cacheMatchesInput;
  const bothCachesMatchInput = cacheMatchesInput && slowCacheMatchesInput;

  // Row click dispatcher — closes the panel, then delegates to the shared
  // handler that matches this row's entity type.
  const { selectItem } = useSearchItemNav({ onBeforeNavigate: () => setOpen(false) });

  // Enter-without-a-selected-row (and the footer button) hand off to the full
  // results PAGE. Both paths go through here so the palette can't drift into
  // two different ideas of what "see everything" means.
  const showAllResults = useCallback(
    (rawQuery: string, source: 'dropdown-enter' | 'dropdown-see-all') => {
      const trimmed = rawQuery.trim();
      if (trimmed.length < MIN_QUERY_LENGTH) return;
      setOpen(false);
      // Carry the palette's filters into the page so the jump doesn't silently
      // widen what you were looking at.
      const params = new URLSearchParams({ q: trimmed });
      navigate(`/app/search?${params.toString()}`, { state: { source } });
    },
    [navigate],
  );

  // "Go to" quick-nav: static registry of settings/nav pages, filtered by
  // the user's permissions/plan/branding, then matched against the query.
  // Instant (no async) — renders as soon as the query hits 2 characters.
  // Cross-language via hardcoded `titleAllLocales` on each registry entry.
  const visibleNavTargets = useVisibleNavTargets();
  // Split so ACTIONS get reserved slots instead of competing with their own
  // destination page for one capped list — see `matchNavTargetsGrouped`.
  const { actions: actionHits, navs: navHits } = matchNavTargetsGrouped(query, visibleNavTargets);
  // Union — gates the "No results" state and the separator below, both of
  // which care whether the palette offered ANY quick-nav row.
  const hasNavHits = actionHits.length > 0 || navHits.length > 0;
  const actionLabels = getActionLabels(actionHits, t);

  // "Recent" section — fills the otherwise-blank panel on open with the last
  // few things the user opened FROM search. Client-side only (localStorage,
  // keyed per user); there is no backend view-history API.
  const hydrateRecents = useSearchRecentsStore((s) => s.hydrate);
  const recordNavTarget = useSearchRecentsStore((s) => s.recordNavTarget);
  const hasRecents = useSearchRecentsStore((s) => s.items.length > 0);

  /**
   * Anchor to the trigger's RIGHT edge and grow leftwards.
   *
   * Right-aligning is the whole trick: the collapsed bar and the expanded one
   * share an edge, so opening reads as that bar widening in place. Anchoring
   * the LEFT edge instead (and clamping) shoved the panel flush against the
   * viewport, which lined up with nothing.
   */
  const measureAnchor = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.min(PANEL_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
    // Keep the trigger's right edge, unless growing leftwards from it would
    // push the left edge off-screen (narrow viewports) — then give up just
    // enough of the alignment to stay on screen.
    const right = Math.min(
      window.innerWidth - rect.right,
      Math.max(0, window.innerWidth - width - VIEWPORT_MARGIN),
    );
    setAnchor({ top: rect.top, right, width, height: rect.height, collapsedWidth: rect.width });
  }, []);

  // Layout effect so the panel paints at the right place on the first frame —
  // a post-paint measure would flash it at 0,0.
  useLayoutEffect(() => {
    if (!open) {
      setExpanded(false);
      return;
    }
    measureAnchor();
    // Next frame, not this one: the browser needs to paint the collapsed width
    // before the transition has anything to animate from.
    const raf = requestAnimationFrame(() => setExpanded(true));
    return () => cancelAnimationFrame(raf);
  }, [open, measureAnchor]);

  // Follow the trigger if the window resizes or an ancestor scrolls under it.
  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', measureAnchor);
    window.addEventListener('scroll', measureAnchor, true);
    return () => {
      window.removeEventListener('resize', measureAnchor);
      window.removeEventListener('scroll', measureAnchor, true);
    };
  }, [open, measureAnchor]);

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        togglePalette();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [togglePalette]);

  // Focus input when panel opens, clear query when it closes
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        // Handoff from the results-page field: land the caret where it was in
        // that input instead of jumping to the end, so editing mid-string
        // ("test" -> "te|st") doesn't teleport the cursor on the first keystroke.
        const caret = handoffCaret.current;
        handoffCaret.current = null;
        if (caret !== null) el.setSelectionRange(caret, caret);
      }, 0);
      // Re-read the signed-in user's recents. Idempotent per user, so this is
      // also how an account switch mid-session picks up the right list.
      hydrateRecents();
    } else {
      setQuery('');
    }
  }, [open, hydrateRecents]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      // Radix portals dropdown content to document.body, so a click on a type
      // in the filter menu lands OUTSIDE `panelRef` and used to read as
      // click-outside — the palette closed the moment you picked a filter.
      if (target?.closest?.('[data-radix-popper-content-wrapper],[role="menu"]')) return;
      if (panelRef.current && !panelRef.current.contains(target as Node)) {
        closePalette();
      }
    };
    // Delay listener so the trigger click doesn't immediately close
    const id = setTimeout(() => document.addEventListener('mousedown', handleClick), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open, closePalette]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closePalette();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closePalette]);

  // Debounced search
  const handleValueChange = useCallback(
    (value: string) => {
      const valueSanitized = sanitizeInput(value, { maxLength: MAX_QUERY_LENGTH });
      setQuery(valueSanitized);
      setSelectedValue(NO_SELECTION);
      hasArrowNavigated.current = false;
      hasInteracted.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      // On the results page the palette is editing that page's query, not just
      // its own dropdown — keep `?q=` following along so the results behind it
      // re-search live. Runs before the min-length bail below: deleting down to
      // one character has to clear the page too, not freeze it on the last hit.
      if (isResultsPage) schedulePageQueryPush(valueSanitized);
      // Atlas Search autocomplete requires min 2 chars
      if (valueSanitized.trim().length < MIN_QUERY_LENGTH) return;
      debounceRef.current = setTimeout(() => {
        search(valueSanitized.trim());
      }, 300);
    },
    [search, isResultsPage, schedulePageQueryPush],
  );

  /**
   * Empty the query without closing the palette.
   *
   * Goes through `handleValueChange('')` rather than `setQuery('')` so the
   * cmdk selection state resets the same way typing does — clearing straight
   * to state left `selectedValue` pointing at a row that no longer exists, so
   * the next Enter fired the stale row's `onSelect`.
   */
  const clearQuery = useCallback(() => {
    handleValueChange('');
    inputRef.current?.focus();
  }, [handleValueChange]);

  /**
   * Editing the results-page field EXPANDS into the palette rather than staying
   * a lone text box: the bar grows and the dropdown drops its top hits below,
   * exactly as it does from the collapsed trigger — you just start from the
   * query that's already on screen. Two things move on the first keystroke:
   *   - the palette adopts the text (seeded + searched immediately, no debounce,
   *     so the dropdown isn't blank for a beat), and
   *   - `?q=` still gets its debounced push, so the page keeps re-searching
   *     underneath while the dropdown sits over it.
   * From here on the palette's own input owns the typing — same value, same
   * two destinations (see `handleValueChange`).
   */
  const expandFromPageField = useCallback(
    (raw: string, caret: number | null) => {
      const value = sanitizeInput(raw, { maxLength: MAX_QUERY_LENGTH });
      handoffCaret.current = caret;
      setQuery(value);
      if (value.trim().length >= MIN_QUERY_LENGTH) search(value.trim());
      openPalette();
    },
    [openPalette, search],
  );

  /**
   * Clicking the field is enough to expand it — no keystroke required. Focus
   * would fire on Tab too, which would pop the palette open while someone is
   * merely tabbing through the nav, so this hangs off the CLICK.
   *
   * Nothing is pushed to `?q=` here: the query hasn't changed, and a write would
   * add a history entry for "user looked at the box".
   */
  const handlePageFieldClick = useCallback(() => {
    expandFromPageField(pageQuery, pageInputRef.current?.selectionStart ?? null);
  }, [expandFromPageField, pageQuery]);

  const handlePageQueryChange = useCallback(
    (raw: string, caret: number | null) => {
      expandFromPageField(raw, caret);
      // The page keeps re-searching underneath while the dropdown sits over it.
      schedulePageQueryPush(sanitizeInput(raw, { maxLength: MAX_QUERY_LENGTH }));
    },
    [expandFromPageField, schedulePageQueryPush],
  );

  // `search` is imperative (useLazyQuery), so unlike the results page nothing
  // refetches when the ordering changes — the sort menu has to re-fire the
  // current query itself, or the panel keeps showing the previous order.
  const searchRef = useRef(search);
  searchRef.current = search;
  // Order groups with current page entity first. `HIDDEN_ENTITY_TYPES`
  // (see `entity-types.ts`) drops disabled buckets from the render loop
  // while leaving the backend fetch / response types intact.
  const currentEntity = getCurrentEntityType(location.pathname);
  const visibleGroups = GROUP_ORDER.filter(isEntityTypeVisible);
  const orderedGroups = currentEntity
    ? [currentEntity, ...visibleGroups.filter((g) => g !== currentEntity)]
    : [...visibleGroups];

  const groupLabels: Record<string, string> = {
    screens: t('Screens'),
    assets: t('Files/Assets'),
    playlists: t('Playlists'),
    schedules: t('Schedules'),
    operationalSchedules: t('Operational Schedules'),
    folders: t('Folders'),
    feeds: t('Feeds'),
    engages: t('Engage'),
    teams: t('Teams'),
    members: t('Members'),
  };

  const groupIcons: Record<string, React.ReactNode> = {
    screens: <Monitor className="mt-0.5 mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    assets: <Image className="mt-0.5 mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    playlists: <ListVideo className="mt-0.5 mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    schedules: <Calendar className="mt-0.5 mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    operationalSchedules: (
      <CalendarClock className="mt-0.5 mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
    ),
    folders: <Folder className="mt-0.5 mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    feeds: <Rss className="mt-0.5 mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    engages: <Smartphone className="mt-0.5 mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    teams: <Users2 className="mt-0.5 mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    members: <User className="mt-0.5 mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
  };

  function getGroupItems(entityType: string): Array<Record<string, unknown>> | null {
    // Assets and engages come from the parallel slow query. Return null while
    // the slow query is in flight — otherwise Apollo's `fetchPolicy: 'network-only'`
    // keeps the PREVIOUS search's data populated until the new response lands,
    // causing stale assets/engages rows (with wrong names and wrong
    // parentGroupInfo) to render alongside fresh screens/playlists rows. The
    // renderGroup() spinner path handles the visible loading state — this just
    // makes sure no stale row can slip through if the spinner path is refactored.
    if (entityType === 'assets') {
      if (assetsEngagesLoading) return null;
      const items = assetsEngagesResults?.assets as Array<Record<string, unknown>> | undefined;
      return items && items.length > 0 ? items : null;
    }
    if (entityType === 'engages') {
      if (assetsEngagesLoading) return null;
      const items = assetsEngagesResults?.engages as Array<Record<string, unknown>> | undefined;
      return items && items.length > 0 ? items : null;
    }
    if (!results) return null;
    // Schedules and operationalSchedules are now DISTINCT backend buckets —
    // no more client-side split by `type === 'Ops'`.
    const items = results[entityType as keyof typeof results] as Array<Record<string, unknown>>;
    return items?.length > 0 ? items : null;
  }

  /**
   * One quick-nav group (Actions / Go to). Same row shape as `renderGroup`'s
   * entity rows — icon slot + single truncated line — so the three groups read
   * as one list. Returns null when empty so the caller stays declarative.
   */
  function renderQuickNavGroup(
    targets: NavTarget[],
    iconFor: (target: NavTarget) => LucideIcon,
    labelFor: (target: NavTarget) => string,
  ) {
    if (targets.length === 0) return null;
    return (
      // No heading. In a palette this size a two-row group under a bold label
      // spends more height on the label than the rows, and both kinds of row
      // now say what they are: an action reads "Create Playlist", a destination
      // reads "Go to Playlists". The entity sections below still keep theirs —
      // "Screens" over five screen names is doing real work.
      <CommandGroup>
        {targets.map((target) => {
          const Icon = iconFor(target);
          return (
            <CommandItem
              key={target.id}
              value={`nav-${target.id}`}
              data-testid={`global-search-nav-${target.id}`}
              className="group min-h-9"
              onSelect={() => {
                recordNavTarget(target.id);
                setOpen(false);
                // Quick-nav v2: a target may carry a store action / URL-param
                // handoff, not just a route.
                const { route, storeAction } = resolveNavAction(target, apolloClient);
                navigate(route);
                storeAction?.();
              }}
            >
              {/* Wrapper matches the entity-icon slot (mr-2 h-4 w-4 shrink-0) so
                  text alignment stays; the glyph itself is smaller
                  (`!h-3.5 !w-3.5` overrides cmdk's forced `size-4`). */}
              <span
                className="mr-2 flex h-4 w-4 shrink-0 items-center justify-center"
                aria-hidden="true"
              >
                <Icon className="!h-3.5 !w-3.5 text-muted-foreground" />
              </span>
              <div className="flex min-w-0 flex-col">
                <span className="truncate">
                  <HighlightText text={labelFor(target)} query={query} />
                </span>
              </div>
              <EnterHint />
            </CommandItem>
          );
        })}
      </CommandGroup>
    );
  }

  function renderGroup(entityType: string) {
    // Progressive rendering: while the slow (assets+engages) query is
    // in flight, HIDE those sections entirely — no header, no rows. A
    // single "Loading..." footer at the bottom of the results (rendered
    // outside this function, see below the orderedGroups.map) covers
    // the slow bucket. Less visual noise than one spinner per hidden
    // section, and avoids the stale-data risk of Apollo's
    // `fetchPolicy: 'network-only'` keeping the previous search's rows
    // in `assetsEngagesResults` until the new response lands.
    if ((entityType === 'assets' || entityType === 'engages') && assetsEngagesLoading) {
      return null;
    }

    const items = getGroupItems(entityType);
    if (!items) return null;

    return (
      <CommandGroup key={entityType} heading={groupLabels[entityType]}>
        {items.map((item, index) => (
          <CommandItem
            key={getRowKey(entityType, item, index)}
            value={`${entityType}-${getRowKey(entityType, item, index)}`}
            data-testid={`global-search-row-${entityType}-${getRowKey(entityType, item, index)}`}
            onSelect={() => selectItem(entityType, item)}
            // `group` is what lets the trailing ⏎ chip reveal itself from
            // cmdk's `data-selected` — see `EnterHint`.
            //
            // `items-start` because these rows are two lines now: a centred glyph
            // floated between the title and the subtitle, pointing at neither.
            // The icons carry `mt-0.5` to sit on the title's cap height rather
            // than its line box.
            className="group min-h-9 items-start"
          >
            {groupIcons[entityType]}
            <div className="flex min-w-0 flex-col">
              <span className="truncate">
                <HighlightText text={getItemName(entityType, item, t)} query={query} />
              </span>
              {/* Where it lives + when it changed, the two things that tell two
                  same-named results apart. Only for types that HAVE a folder and
                  an authoring date — feeds are curated templates with neither,
                  and folders/teams/members keep their own subtitles below. */}
              <RowSubtitle
                segments={rowSubtitleSegments(entityType, item, t, activeTeamId)}
                workspaceName={workspaceNameFor(entityType, item)}
              />
              {entityType === 'feeds' && (item.author as string) && (
                <span className="truncate text-xs text-muted-foreground">
                  {item.author as string}
                </span>
              )}
              {entityType === 'teams' && typeof item.memberCount === 'number' && (
                <span className="truncate text-xs text-muted-foreground">
                  {`${item.memberCount as number} ${t('Members')}`}
                </span>
              )}
              {entityType === 'members' && (
                <MemberSubtitle
                  email={item.email as string | undefined}
                  teamspaceName={memberTeamName(item)}
                />
              )}
            </div>
            <EnterHint />
          </CommandItem>
        ))}
      </CommandGroup>
    );
  }

  return (
    <>
      {/* Trigger in nav bar. The wrapper exists purely to be measured — the
          panel anchors to this box (see `measureAnchor`). */}
      <div
        ref={triggerRef}
        className={cn(
          'inline-flex transition-opacity duration-200 ease-out motion-reduce:transition-none',
          isHandedToPage && 'pointer-events-none opacity-0',
        )}
        aria-hidden={isHandedToPage}
      >
        {showPageField ? (
          /* Same chrome as the collapsed trigger it replaces (`h-9 w-64`,
             `rounded-sm`, `border-border`, `bg-card`) so the nav doesn't shift
             when you land on the results page — it's the same box, now typeable.
             Padding is `pl-3 pr-1.5` for the reason the palette bar uses it: the
             trailing icon-button carries 6px of its own inset. */
          <div
            /* No resting shadow: a permanent `shadow-sm` made an idle box look
               lifted and busy. The lift now only appears while it's in use —
               hover or focus — matching the collapsed trigger's hover-only
               shadow on every other page. */
            /* Grey at rest, white on focus — Google's search-field treatment.
               `sidebar-active` (#e2e6f1), NOT `muted` (#f6f7fb): the page behind
               the card and the nav itself are already sitting at muted, so a
               muted field was invisible against them. This is the palette's next
               step down, and in dark mode it inverts correctly — #2e3244 is
               LIGHTER than the panel, which is the same "stands out" intent.
               A white box on a white nav has to be drawn with a border to exist
               at all; a soft grey fill reads as an input on its own, so the
               border goes transparent and the field stops competing with the
               page. Focus flips it to `bg-card` + ring, which is what makes
               "I'm typing here" unmistakable. */
            className={cn(
              // Hover DARKENS THE BORDER, it doesn't cast a shadow. A drop
              // shadow under a filled grey box reads as embossed, not raised —
              // shadows are for surfaces that float above the page, and a field
              // sits in it. Same convention as the `trigger` Button variant in
              // @scio3/ui (darker border on hover, no fill change). Focus is the
              // real state change: white + ring.
              'flex h-9 items-center gap-2 rounded-lg border border-transparent bg-sidebar-active pl-3 pr-1.5 transition-[background-color,border-color,width] hover:border-muted-foreground/30 focus-within:border-ring focus-within:bg-card',
              wide ? 'w-96' : 'w-64',
            )}
            data-testid="global-search-page-field"
          >
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input
              ref={pageInputRef}
              data-testid="global-search-page-input"
              className="h-full w-full min-w-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
              placeholder={`${t('Search')}...`}
              value={pageQuery}
              maxLength={MAX_QUERY_LENGTH}
              onChange={(e) => handlePageQueryChange(e.target.value, e.target.selectionStart)}
              onClick={handlePageFieldClick}
              onKeyDown={(e) => {
                if (isEnterKey(e)) {
                  e.preventDefault();
                  flushPageQuery();
                }
                // Escape gives the page back the keyboard. It must NOT bubble to
                // the palette's document-level Escape handler.
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  e.currentTarget.blur();
                }
              }}
            />
            {pageQuery && (
              <Button
                variant="ghost"
                size="icon-xs"
                className="shrink-0 text-muted-foreground hover:text-foreground"
                onClick={clearPageQuery}
                aria-label={t('Clear')}
                data-testid="global-search-page-clear"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        ) : compact ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-sidebar-active/60"
                  aria-label={t('Search')}
                  data-testid="global-search-trigger"
                  onClick={openPalette}
                >
                  <Search className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="flex items-center gap-1">
                {t('Search')}
                <kbd className="inline-flex items-center gap-0.5 rounded border bg-muted px-1 font-mono text-[10px] font-medium text-muted-foreground">
                  <span className="text-xs">&#8984;</span>K
                </kbd>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  // Hover reads as a lift, not a weight change: the shadow is the only
                  // thing that moves. `font-normal` drops the Button base's
                  // `font-medium` — which made the resting label look heavier than
                  // the expanded bar's own text — and dropping `hover:text-foreground`
                  // stops the label darkening into a pseudo-bold under the cursor.
                  // Same grey fill as the editable field on the results page,
                  // so the control doesn't change colour when you cross onto
                  // that page — it only gains a caret.
                  className="h-9 w-64 justify-start gap-2 rounded-lg border border-transparent bg-sidebar-active font-normal text-muted-foreground transition-[border-color] hover:border-muted-foreground/30 hover:bg-sidebar-active hover:text-muted-foreground"
                  data-testid="global-search-trigger"
                  onClick={openPalette}
                >
                  <Search className="h-3.5 w-3.5 shrink-0" />
                  <span className="text-sm flex-1 text-left">{t('Search')}...</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="flex items-center gap-1">
                {t('Search')}
                <kbd className="inline-flex items-center gap-0.5 rounded border bg-muted px-1 font-mono text-[10px] font-medium text-muted-foreground">
                  <span className="text-xs">&#8984;</span>K
                </kbd>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* TWO surfaces sharing one anchor, not one card with an input baked in:
          an expanded search BAR sitting exactly where the collapsed trigger is
          (same top, same height, same right edge — so it reads as that bar
          widening), and the results dropdown hanging below it at the same
          width. A single tall card with its own oversized input looked like a
          new box appearing on top of the nav rather than the nav's own search
          growing.
          One `Command` wraps both so cmdk's keyboard nav still spans input and
          list. Portaled to document.body so ancestor stacking contexts (e.g. a
          page-level `<PlanGate>` overlay) can't capture it — same escape
          hatch Radix uses for DropdownMenu / Popover content. */}
      {open &&
        createPortal(
          <>
            {/* No backdrop. This is a nav dropdown, not a modal — dimming the
                page made the results feel detached from the bar that opened
                them, and the heavy shadow on the dropdown does the separating
                instead. Click-outside-to-close is the mousedown listener above,
                which never needed a backdrop element to work. */}
            <div
              ref={panelRef}
              data-testid="global-search-panel"
              // Transparent shell at the FULL expanded width — it does not
              // animate. Only the bar inside it grows (leftwards, via `ml-auto`
              // + an animated width); the dropdown pops in at full width so it
              // reads as its own surface appearing rather than something being
              // stretched open by the bar.
              className="fixed z-[60]"
              style={
                anchor
                  ? { top: anchor.top, right: anchor.right, width: anchor.width }
                  : // Pre-measure frame: keep it off-screen rather than flashing
                    // at 0,0. `useLayoutEffect` fills this in before paint.
                    { top: -9999, right: -9999, width: PANEL_WIDTH }
              }
            >
              <Command
                shouldFilter={false}
                value={selectedValue}
                onValueChange={(v) => {
                  if (hasInteracted.current) setSelectedValue(v);
                }}
                // Descendant overrides rather than a className on every row: the
                // rows live in three different files (entity groups here, "Go to",
                // and `RecentSearchGroup`), and scoping from the one `Command`
                // root keeps them from drifting apart. These win over the base
                // classes in @scio3/ui on specificity — `.cmdk [cmdk-item]` beats
                // the item's own single `.px-2`.
                className={cn(
                  // `Command` in @scio3/ui ships `overflow-hidden` + `bg-popover`
                  // + `rounded-md` for the palette shape it was written for. Here
                  // it's just a layout wrapper around two independently-styled
                  // surfaces, and both of those defaults actively broke this:
                  //   - `overflow-hidden` CLIPPED the dropdown's drop shadow (the
                  //     shadow computed fine, you just couldn't see it), and
                  //   - `bg-popover` painted a filled rectangle spanning the bar,
                  //     the gap AND the dropdown, visually re-joining the two
                  //     surfaces that are meant to read as separate.
                  // Same-property overrides, so tailwind-merge drops the originals.
                  'overflow-visible rounded-none bg-transparent',
                  // Three edges to reconcile, so the values are asymmetric on
                  // purpose. Measured from the panel's inner edge (every number
                  // below already includes `CommandGroup`'s 4px `p-1`):
                  //
                  //   hover band starts   12px   (ml-2)
                  //   row icon + text     24px   (pl-3) — 12px inside the band,
                  //                              so the glyph isn't jammed against
                  //                              the highlight's edge
                  //   group heading       16px   (px-3) — outdented, sitting
                  //                              just inside the band's left edge
                  //
                  // The heading is deliberately NOT level with the row text: as a
                  // label for the rows beneath it, it reads better hanging to
                  // their left, the way a section marker sits outside the column
                  // it introduces. Level with the text made it look like another
                  // row that had lost its icon.
                  //
                  // Right side is deliberately tighter (`mr-1` = 8px vs 12px):
                  // OverlayScrollbars floats its bar over that gutter, and it
                  // reads as part of the gap. Trimming 4px there makes the
                  // apparent margins match once the scrollbar is on screen. Padding alone couldn't do this — the highlight paints
                  // the item's whole box, so `px-4` put the band 16px wider than
                  // the heading it sits under. The margin is what pulls the band
                  // in; the padding then puts the text back where it belongs.
                  '[&_[cmdk-item]]:ml-2 [&_[cmdk-item]]:mr-1 [&_[cmdk-item]]:pl-3 [&_[cmdk-item]]:pr-2',
                  '[&_[cmdk-group-heading]]:px-3',
                  // `CommandGroup` ships `p-1`, so two adjacent groups put 4px +
                  // 4px between the last row of one and the first row of the
                  // next — an 8px seam that read as a deliberate break between
                  // Actions and Go to when those groups lost their headings.
                  // Vertical only: the horizontal 4px is load-bearing for the
                  // row geometry above (the band's 12px = this 4px + `ml-2`).
                  '[&_[cmdk-group]]:py-0',
                  // Section headings: darker + a size up so they separate the
                  // list into scannable blocks instead of blending into the rows.
                  // The COLOUR needs the attribute selector doubled: `CommandGroup`
                  // in @scio3/ui sets `[&_[cmdk-group-heading]]:text-muted-foreground`
                  // at the same specificity, so a single-selector override loses
                  // on stylesheet order (size + weight happened to win; colour
                  // didn't). Doubling `[cmdk-group-heading]` outranks it without
                  // reaching for `!important`.
                  '[&_[cmdk-group-heading]]:text-[13px] [&_[cmdk-group-heading]]:font-semibold',
                  '[&_[cmdk-group-heading][cmdk-group-heading]]:text-foreground',
                  // A little more air above each heading than below it, so the
                  // label groups with the rows it introduces. Kept modest (6px):
                  // this padding STACKS with the 4px `p-1` of both the group above
                  // and the group it belongs to, so at pt-2.5 the run-in from the
                  // quick-nav rows to "Files/Assets" totalled ~18px of dead space.
                  '[&_[cmdk-group-heading]]:pt-1.5 [&_[cmdk-group-heading]]:pb-1',
                )}
              >
                {/* The BAR. Same height, right edge and chrome as the collapsed
                  trigger it covers (`h-9`, `rounded-sm`, `border-border`,
                  `bg-card`) — only wider. Height comes from the measured
                  trigger so the two can't drift if the nav's sizing changes. */}
                <div
                  // `ml-auto` pins it to the shell's right edge, so animating
                  // width grows it leftwards. `transition-[width]`, never
                  // `transition-all`.
                  // Asymmetric padding is deliberate: the leading `Search` is a
                  // bare 14px svg so `pl-3` puts it 12px off the edge, but the
                  // trailing X is an `icon-xs` Button — a 24px box around a 12px
                  // glyph, carrying 6px of its own inset. `pr-1.5` gives back
                  // exactly that 6px so BOTH glyphs land 12px from the border;
                  // a symmetric `px-3` leaves the X looking 6px short of the edge.
                  // `rounded-lg` + `shadow-md` — identical to the dropdown
                  // below it. The two are one floating control split across two
                  // surfaces, so a 6px corner over a 10px one (and a lighter
                  // shadow over a heavier one) read as two unrelated boxes that
                  // happened to land near each other.
                  className="ml-auto flex items-center gap-2 rounded-lg border border-muted-foreground/30 bg-card pl-3 pr-1.5 shadow-md transition-[width] duration-200 ease-out motion-reduce:transition-none"
                  style={{
                    height: compact ? BAR_HEIGHT : (anchor?.height ?? BAR_HEIGHT),
                    width: expanded
                      ? (anchor?.width ?? PANEL_WIDTH)
                      : (anchor?.collapsedWidth ?? 256),
                  }}
                >
                  <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <input
                    ref={inputRef}
                    data-testid="global-search-input"
                    // `text-sm`, matching the collapsed trigger's own label — the
                    // previous `text-base font-medium` belonged to a tall palette
                    // header and would tower over a 36px bar.
                    className="h-full w-full min-w-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                    // The SAME placeholder the collapsed trigger shows. The bar
                    // grows out of that trigger, so swapping in a longer string
                    // mid-animation made text appear from nowhere while the box
                    // was still moving — two changes competing for the eye. The
                    // descriptive version ("Search screens, assets, …") stays on
                    // the results page's own field, which is 640px from the
                    // start and animates from nothing.
                    placeholder={`${t('Search')}...`}
                    value={query}
                    maxLength={MAX_QUERY_LENGTH}
                    onChange={(e) => handleValueChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        hasInteracted.current = true;
                        hasArrowNavigated.current = true;
                      }
                      if (isEnterKey(e) && !hasArrowNavigated.current) {
                        // Typed and hit Enter without arrow-navigating — open the
                        // full results page for the query. If the user HAD
                        // arrow-navigated to a row, cmdk's own Enter handler
                        // fires that row's onSelect instead and takes them
                        // straight to the entity.
                        e.preventDefault();
                        showAllResults(query, 'dropdown-enter');
                      }
                      // If user arrow-navigated, let cmdk handle Enter (fires onSelect)
                    }}
                  />
                  {/* Two DISTINCT actions, which the single X used to conflate:
                    Clear empties the query and keeps the palette open (only
                    shown when there's something to clear), X dismisses the
                    whole palette. Without the X the only way out with a mouse
                    was clicking the backdrop, which isn't discoverable. */}
                  {query && (
                    <Button
                      variant="ghost"
                      size="xs"
                      className="shrink-0 px-1.5 text-xs font-normal text-muted-foreground hover:text-foreground"
                      onClick={clearQuery}
                      data-testid="global-search-clear"
                    >
                      {t('Clear')}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={closePalette}
                    aria-label={t('Close')}
                    data-testid="global-search-close"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {/* The DROPDOWN. Its own surface under the bar, same width, so the
                  pair reads as one search field with its results attached. */}
                <div
                  className={cn(
                    // Always full width — it never stretches with the bar. `mt-2`
                    // keeps a visible gap so it reads as a detached surface.
                    //
                    // Edge first, shadow second. `--color-border` is #edf2f9 in
                    // light mode — so faint against a white card that the panel
                    // had no edge at all, and every bit of separation was coming
                    // from blur. That's what read as muddy. `muted-foreground/30`
                    // draws an actual line (a step up from the /20 the row
                    // hairlines use, since this one has to hold a floating
                    // surface), which lets the shadow drop to `shadow-md` — a
                    // tight 6px lift instead of the 36px-blur haze this started
                    // as (the "heavy scio1 shadow" DESIGN_SYSTEM.md warns off).
                    'mt-2 overflow-hidden rounded-lg border border-muted-foreground/30 bg-card shadow-md',
                    // Pops: fades and rises on the same 200ms as the bar's width,
                    // so both land together without the two being coupled.
                    'transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
                    expanded
                      ? 'translate-y-0 opacity-100'
                      : // `pointer-events-none` matters on the way OUT: the panel
                        // stays mounted for the length of the collapse, and a
                        // half-faded row must not be clickable.
                        '-translate-y-2 opacity-0 pointer-events-none',
                  )}
                  data-testid="global-search-dropdown"
                >
                  <CommandList
                    // Height is FIXED in every state — one panel size, always.
                    //
                    // It has to be fixed during a query: results land in two waves
                    // (the fast bucket paints, then assets/engages ~1s later) and
                    // an auto-height panel would visibly grow under the cursor
                    // mid-read. It is now fixed on the empty-query state too. That
                    // reverses an earlier call to content-size the recents view —
                    // the worry then was a tall white box under two recent rows,
                    // but the cost of avoiding it was worse: the panel snapped
                    // between three different heights (one recent, five recents,
                    // results) as you typed. A stable frame with empty space below
                    // is calmer than a box that resizes on every keystroke, and it
                    // keeps the footer hint anchored in one place.
                    //
                    // `max-h` is NOT redundant with `h`: `CommandList` in @scio3/ui
                    // ships `max-h-[300px]`, and `cn()` can't merge that away
                    // against `h-[…]` because they're different properties — so a
                    // bare `h-[…]` here was silently clamped to 300px. Same-
                    // property `max-h` DOES merge, so the cap below always wins.
                    // The `vh` half is what actually applies on a laptop.
                    // `pt-1.5` is the panel's own top gutter. It used to be
                    // supplied by the first group's HEADING; with the quick-nav
                    // groups headingless, all that remained above the first row
                    // was `CommandGroup`'s 4px `p-1`, which read as the row being
                    // shoved into the panel's top edge. On the list rather than
                    // the group so it applies once, whichever block comes first.
                    // `thin-scrollbar` (globals.css): 4px thumb over a
                    // transparent track. The UA default drew a full-width rail
                    // with its own background, which read as a second border
                    // down the inside edge of the panel.
                    className="thin-scrollbar h-[min(420px,52vh)] max-h-[min(420px,52vh)] pt-1.5 pb-2"
                    onMouseMove={() => {
                      hasInteracted.current = true;
                    }}
                  >
                    {/* Phase-1 reserved spacer used to live here to keep the panel
                  a consistent height while nothing had landed. No longer
                  needed — `CommandList` now has a fixed `h-[…]`, and the
                  spacer was causing "Go to" to jump ~24px once results
                  landed and the spacer un-rendered. */}
                    {/* Recent (empty-query state only). Hidden as soon as the user
                  starts typing — from there on the panel is about the query. */}
                    {!query.trim() && (
                      <RecentSearchGroup
                        onSelectEntity={(entityType, item) => selectItem(entityType, item)}
                        onSelectNav={(route) => {
                          setOpen(false);
                          navigate(route);
                        }}
                      />
                    )}
                    {/* First-run guidance, and ONLY that. It fills a panel that
                        would otherwise open completely blank, where empty reads
                        as "broken" rather than "ready". Once there are recents
                        the panel already has content and a starting point, so
                        the prompt is just a second thing to read past on the way
                        to the row you came back for — it asks a question you
                        have already answered. Copy is the SAME pair the results
                        page shows before you search, so the two surfaces teach
                        the same thing. */}
                    {!query.trim() && !hasRecents && (
                      <PaletteHint
                        variant="prompt"
                        title={t('What are you looking for?')}
                        description={t('Search screens, files, playlists, schedules, and more.')}
                        // The panel's only content in this state, so it centres
                        // in the full height instead of hugging the top.
                        className="h-full justify-center"
                        data-testid="global-search-idle-hint"
                      />
                    )}
                    {!loading && !assetsEngagesLoading && query.trim().length === 1 && (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        {t('Type one more character to search...')}
                      </div>
                    )}
                    {/* "No results" — shown once the cache represents the CURRENT
                  input and holds no rows, and no nav match takes over. No
                  `!loading` gate: re-fetching the same term shouldn't visually
                  invalidate a correct "No results found" (the spinner
                  suppresses itself for the same reason). During the 300ms
                  debounce before the first search, `cachedQuery` still holds
                  the previous query so the gate fails naturally. */}
                    {query.trim().length >= MIN_QUERY_LENGTH &&
                      cachedQuery === trimmedQuery &&
                      !hasResults &&
                      !hasNavHits && (
                        <CommandEmpty>
                          <PaletteHint
                            variant="empty"
                            title={t('No results found for "{{query}}"', { query: trimmedQuery })}
                            description={t('Try fewer words, or check the spelling.')}
                            data-testid="global-search-no-results"
                          />
                        </CommandEmpty>
                      )}
                    {/* Quick-nav, in two groups above the entity results.
                  ACTIONS first: reaching for search to DO something ("pair a
                  screen", "new playlist") is the impatient path, and it used
                  to require guessing the verb the registry happened to use.
                  Both groups match on the shared noun, so "playlist" now
                  surfaces Create Playlist AND Playlists without competing for
                  the same slots. */}
                    {query.trim().length >= MIN_QUERY_LENGTH &&
                      renderQuickNavGroup(
                        actionHits,
                        (target) => getActionIcon(target.openDialog),
                        // Verb only — "Create Playlist" already reads as an action.
                        (target) => actionLabels.get(target.id) ?? t(target.titleEnglish),
                      )}
                    {query.trim().length >= MIN_QUERY_LENGTH &&
                      renderQuickNavGroup(
                        navHits,
                        () => ArrowRight,
                        // The verb moves INTO the row now that the group heading
                        // is gone: "Go to Settings › Billing". The crumb stays —
                        // it's what makes a page unambiguous.
                        (target) =>
                          t('Go to {{label}}', {
                            label: target.crumbs.map((c) => t(c)).join(' › '),
                          }),
                      )}
                    {/* Results block — rendered once at least one query has landed
                  with content. During Phase 1 the reserved-space block above
                  keeps the panel from collapsing; the always-on footer below
                  provides the visible loading signal. */}
                    {/* Length gate: below MIN_QUERY_LENGTH no new search fires,
                  so the hook's `previousData` still holds the last payload —
                  hide it rather than render stale rows under the "Type one
                  more character…" banner. Above the threshold we keep serving
                  cache while a new query is in flight so the swap is in place,
                  with no flash. */}
                    {query.trim().length >= MIN_QUERY_LENGTH && displayHasResults && (
                      <>
                        {/* No rules between groups: each group already announces
                      itself with a heading, and a line under every one of them
                      banded a short list into a stack of boxes. This also
                      retires the null-group bookkeeping the old index-based
                      separator needed to avoid a stray leading rule. */}
                        {orderedGroups.map((entityType) => (
                          <div key={entityType}>{renderGroup(entityType)}</div>
                        ))}
                      </>
                    )}
                    {/* Single always-on "Searching..." footer while ANY query is
                  in flight — the only loading indicator in the panel. Fires
                  during Phase 1 (nothing landed yet) and Phase 2 (fast
                  landed, slow still loading). No separator above — the fixed
                  panel height + the always-visible footer border below
                  already frame the section, and a hairline directly above
                  the spinner reads as noise.
                  Cache-hit skip: when the input matches what the cache already
                  represents, the spinner would cover a correct result for a
                  beat and then reveal the same answer — a needless "did I lose
                  the results?" flicker. */}
                    {(loading || assetsEngagesLoading) &&
                      query.trim().length >= MIN_QUERY_LENGTH &&
                      cachedQuery !== trimmedQuery && (
                        <div
                          className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"
                          data-testid="global-search-loading-more"
                        >
                          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                          <span>{t('Searching...')}</span>
                        </div>
                      )}
                  </CommandList>
                  {/* Footer — keyboard hints on the left, the full-results
                escape hatch on the right. Sits OUTSIDE CommandList so it stays
                put while the list scrolls, and it always renders so the panel
                height never jumps as results land.
                The hints are honest: ↑↓ and ⏎ are cmdk's own handlers, and
                Esc is the listener above. */}
                  {(() => {
                    const trimmed = query.trim();
                    // Progressive: offer the jump as soon as EITHER bucket lands.
                    // Waiting for both was over-cautious — "see everything" is
                    // meaningful before the slow assets/engages bucket returns.
                    // `bothCachesMatchInput` keeps the jump from arming against
                    // a stale payload while a new query is still resolving.
                    const canShowAll =
                      trimmed.length >= MIN_QUERY_LENGTH && hasResults && bothCachesMatchInput;
                    return (
                      <div className="flex items-center justify-between gap-3 border-t px-4 py-2 text-xs text-muted-foreground">
                        <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
                          <KeyHint keys={['↑', '↓']} label={t('to navigate')} />
                          {/* Names the key as well as showing it. `Esc` is a
                              word on its own chip so "Esc to close" reads as a
                              sentence; `↵` is a glyph, and "↵ to select" left
                              the reader to name it themselves. */}
                          <KeyHint keys={['↵']} label={t('Enter to select')} />
                          <KeyHint keys={['Esc']} label={t('to close')} />
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn('h-7 shrink-0 gap-1.5', !canShowAll && 'invisible')}
                          data-testid="global-search-see-all"
                          disabled={!canShowAll}
                          aria-hidden={!canShowAll}
                          tabIndex={canShowAll ? 0 : -1}
                          onClick={
                            canShowAll
                              ? () => showAllResults(trimmed, 'dropdown-see-all')
                              : undefined
                          }
                        >
                          <Search className="h-3.5 w-3.5 shrink-0" />
                          <span>{t('All results')}</span>
                        </Button>
                      </div>
                    );
                  })()}
                </div>
              </Command>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

/**
 * Second line on a palette result: "Marketing · Edited 3 days ago".
 *
 * Both halves are optional — a root-level item has no folder, and some rows
 * carry no usable date — so the parts are filtered before joining and the whole
 * line disappears when neither survives, rather than leaving a stray separator.
 *
 * Scoped to types where both facts exist and matter. Folders, teams and members
 * already print their own more specific subtitle (kind, member count, email), and
 * feeds are OptiSigns-curated templates with no folder and no actionable date.
 */
/**
 * Types whose subtitle may name an owning teamspace: the six with a folder + date, plus `folders`
 * (which prints its own trail and takes the teamspace after it).
 *
 * Excludes `teams` (a team row's `teamId` IS the team), `members` (that `teamId` is the membership's
 * team, and the row already prints it) and `feeds` (curated, no owning teamspace) — stating a
 * teamspace on those would be wrong, not just noisy. Same reasoning as the results page's
 * `WORKSPACE_COLUMN_TYPES`.
 */
const WORKSPACE_SUBTITLE_TYPES = new Set<string>([
  'screens',
  'assets',
  'engages',
  'playlists',
  'schedules',
  'operationalSchedules',
  'folders',
]);

const SUBTITLE_ENTITY_TYPES = new Set<string>([
  'screens',
  'assets',
  'engages',
  'playlists',
  'schedules',
  'operationalSchedules',
]);

/**
 * The two TEXT halves of a palette row's second line, kept separate so the caller can render the
 * owning teamspace BETWEEN them with its own glyph.
 *
 * Returning a joined string was simpler but made the icon impossible: the teamspace has to sit after
 * the folder (the more specific answer to "where") and before the date, i.e. in the middle of the
 * line — so the parts have to reach the renderer unjoined.
 */
interface RowSubtitleSegments {
  /** Folder location, or a folder row's own trail. */
  lead: string | null;
  /** "Edited 3 days ago". Never set for folder rows. */
  recency: string | null;
}

function rowSubtitleSegments(
  entityType: string,
  item: Record<string, unknown>,
  t: ReturnType<typeof useTranslation>['t'],
  activeTeamId: string,
): RowSubtitleSegments {
  // Folders show WHERE THEY LIVE, rooted at their section: "Screens / ... /
  // Floor 2". That replaced the folder's kind ("Screen Folder"), which the
  // first crumb now says anyway — and a kind can't tell two folders of the
  // same name apart, which is the whole job of this line.
  if (entityType === 'folders') {
    // Cross-team folder = accessed via folder-share, so ancestor names in
    // `path` may belong to folders the user has no access to (privacy leak).
    // Mirrors `FolderBreadcrumb` (`fromShared` + `sharedRoot`), which trims
    // everything above the accessible entry point. We don't have the entry
    // point here — the search result carries no ancestor-security info —
    // so we hide the whole trail and show just "Shared with me", matching
    // the sidebar location the user would enter to reach this folder.
    const folderTeamId = item.teamId as string | undefined;
    if (activeTeamId && folderTeamId && folderTeamId !== activeTeamId) {
      return { lead: t('Shared with me'), recency: null };
    }
    const groupLabels = getGroupLabels(t);
    const trail = collapseTrailSegments(
      getFolderTrailSegments(item, (section) => groupLabels[section]),
    );
    return { lead: trail.length > 0 ? trail.join(' / ') : null, recency: null };
  }
  if (!SUBTITLE_ENTITY_TYPES.has(entityType)) return { lead: null, recency: null };
  const path = getContainerPath(entityType as SearchEntityType, item);
  // Full breadcrumb, middle collapsed — "Priorities List / ... / Q3". The leaf
  // alone was ambiguous: every nested folder called "Q3" read identically no
  // matter which area of the app it sat in.
  const trail = collapseLocationTrail(path);
  const location = path === null ? null : trail.length === 0 ? t('Home') : trail.join(' / ');
  const recency = getRecencyShort(entityType, item);
  return {
    lead: location,
    recency: recency ? t('Edited {{when}}', { when: recency }) : null,
  };
}

/**
 * A teamspace named INLINE — `Building2` glyph + name, inheriting the surrounding text size.
 *
 * The glyph is the point: every other surface that names a teamspace pairs it with this same icon
 * (`WorkspaceCell` in the results-table column and on all five list surfaces, `WorkspaceBadge` on
 * cards and sidebar rows), so a bare "Main Teamspace" in the middle of a subtitle read as just more
 * grey text — indistinguishable from the folder name sitting next to it, which is exactly the
 * inconsistency Ted flagged. Not `WorkspaceBadge` itself: that is a filled chip, far too heavy for a
 * 12px line that already carries two other facts.
 */
function TeamspaceInline({ name }: { name: string }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1 align-middle">
      <Building2 className="h-3 w-3 shrink-0" aria-hidden />
      <span className="truncate">{name}</span>
    </span>
  );
}

/**
 * A member row's second line: `email · [glyph] teamspace`.
 *
 * The teamspace half used to print `t('Default Team')` for the root teamspace, whose English string
 * is "Main Team" — so a member row read "Main Team" while the content row above it read "Main
 * Teamspace". It now takes the SAME resolved name and the SAME glyph as every other teamspace in
 * the palette.
 */
function MemberSubtitle({
  email,
  teamspaceName,
}: {
  email?: string;
  teamspaceName: string | null;
}) {
  if (!email && !teamspaceName) return null;
  return (
    <span className="flex min-w-0 items-center gap-1 truncate text-xs text-muted-foreground">
      {email && <span className="truncate">{email}</span>}
      {teamspaceName && (
        <>
          {email && <span aria-hidden>·</span>}
          <TeamspaceInline name={teamspaceName} />
        </>
      )}
    </span>
  );
}

/**
 * A palette row's second line: `folder · [glyph] teamspace · Edited 3 days ago`.
 *
 * Separators are rendered BETWEEN surviving parts rather than baked into the strings, so a row with
 * no folder, or no teamspace (outside All view), or no date never shows a stray "·".
 */
function RowSubtitle({
  segments,
  workspaceName,
}: {
  segments: RowSubtitleSegments;
  workspaceName: string | null;
}) {
  const parts: React.ReactNode[] = [];
  if (segments.lead) parts.push(segments.lead);
  if (workspaceName) parts.push(<TeamspaceInline key="ws" name={workspaceName} />);
  if (segments.recency) parts.push(segments.recency);
  if (parts.length === 0) return null;

  return (
    <span className="flex min-w-0 items-center gap-1 truncate text-xs text-muted-foreground">
      {parts.map((part, index) => (
        <span key={index} className="inline-flex min-w-0 items-center gap-1 truncate">
          {index > 0 && <span aria-hidden>·</span>}
          {part}
        </span>
      ))}
    </span>
  );
}

/**
 * Centred glyph-in-a-tile over a title and a line of guidance — the palette's
 * idle and no-results states.
 *
 * A plain lucide glyph on white read as an error marker; sitting it in a soft
 * rounded tile makes the same icon read as an illustration, which is the
 * difference between "something went wrong" and "nothing here yet". Deliberately
 * not the page's `SearchEmptyState`: that one is built to FILL a pane
 * (`min-h-full flex-1`), and inside the palette's fixed-height list it would
 * push the rows above it off-screen.
 */
function PaletteHint({
  variant,
  title,
  description,
  className,
  ...rest
}: {
  variant: 'prompt' | 'empty';
  title: string;
  description: string;
  className?: string;
  'data-testid'?: string;
}) {
  return (
    <div
      className={cn(
        // Centred in the list's own height, not parked at the top of it.
        // `CommandList` is a fixed `h-[min(420px,52vh)]` scroller, so a hint
        // sized to its content sat under the input with ~250px of blank sheet
        // below it. `min-h` a little under the list (its `pt-1.5` plus
        // `CommandEmpty`'s `py-6`) fills that box without making it scroll.
        'flex min-h-[min(356px,44vh)] flex-col items-center justify-center gap-4 px-6 text-center',
        className,
      )}
      data-testid={rest['data-testid']}
    >
      <SearchStateArt variant={variant} />
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="max-w-[280px] text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
